import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Budget } from './pages/public/Budget';
import { DatasetDetail } from './pages/public/DatasetDetail';
import { Datasets } from './pages/public/Datasets';
import { Foi } from './pages/public/Foi';
import { FoiStatus } from './pages/public/FoiStatus';
import { Home } from './pages/public/Home';
import { Integrity } from './pages/public/Integrity';
import { NoticeDetail } from './pages/public/NoticeDetail';
import { Notices } from './pages/public/Notices';
import { Parcels } from './pages/public/Parcels';
import { SignInComplete } from './pages/staff/SignInComplete';
import { StaffDatasets } from './pages/staff/StaffDatasets';
import { StaffFoi } from './pages/staff/StaffFoi';
import { StaffHome } from './pages/staff/StaffHome';
import { StaffLogin } from './pages/staff/StaffLogin';
import { StaffNotices } from './pages/staff/StaffNotices';
import { PublicLayout } from './ui/PublicLayout';
import { Shell } from './ui/Shell';
import { Spinner } from './ui/components';

const STAFF_NAV = [
  { to: '/staff', label: 'Overview', icon: '◫' },
  { to: '/staff/foi', label: 'FOI requests', icon: '◆' },
  { to: '/staff/datasets', label: 'Datasets & budget data', icon: '▤' },
  { to: '/staff/notices', label: 'Public notices', icon: '▧' },
];

/**
 * Gate for the `/staff/*` subtree only. Everything under `PublicLayout`
 * below renders unconditionally, with no persona check at all — this is the
 * one thing every other app in this repo does differently (they all show a
 * Login screen whenever `!persona`, gating the entire app). OpenGov is a
 * public accountability site first; only the MDA staff console needs a
 * signed-in persona, so the gate lives here, scoped to this one subtree,
 * instead of wrapping <App/> itself.
 */
function StaffGate() {
  const { persona, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-950">
        <Spinner label="Loading staff console…" />
      </div>
    );
  }

  if (!persona) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/staff/login?returnTo=${returnTo}`} replace />;
  }

  return <Shell title="MDA Staff Console" items={STAFF_NAV} />;
}

export function App() {
  return (
    <Routes>
      {/* ── Public transparency site — reachable by anyone, no sign-in required ── */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/datasets" element={<Datasets />} />
        <Route path="/datasets/:code" element={<DatasetDetail />} />
        <Route path="/budget" element={<Budget />} />
        <Route path="/foi" element={<Foi />} />
        <Route path="/foi/:ref" element={<FoiStatus />} />
        <Route path="/notices" element={<Notices />} />
        <Route path="/notices/:id" element={<NoticeDetail />} />
        <Route path="/integrity" element={<Integrity />} />
        {/* Gated on being signed in (any persona, any role) — not on the
            DATA_STEWARD/FOI_OFFICER staff roles below — because GeoServe's
            backend only requires its lowest identity tier (T1). See
            pages/public/Parcels.tsx for the full disclosure shown to a
            visitor who isn't signed in yet. */}
        <Route path="/parcels" element={<Parcels />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* ── MDA staff console — gated to DATA_STEWARD:<mda> / FOI_OFFICER ── */}
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/staff/signin/complete" element={<SignInComplete />} />
      <Route path="/staff" element={<StaffGate />}>
        <Route index element={<StaffHome />} />
        <Route path="foi" element={<StaffFoi />} />
        <Route path="datasets" element={<StaffDatasets />} />
        <Route path="notices" element={<StaffNotices />} />
        <Route path="*" element={<Navigate to="/staff" replace />} />
      </Route>
    </Routes>
  );
}
